import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'flag-icons/css/flag-icons.min.css';
import './HistorialPage.css';
import axios from 'axios';
import { formatDate, getResultLabel, getConfidenceColor } from '../utils/formatters';
import { PredictionDetailModal } from '../components/shared/PredictionDetailModal';

const SURFACE_COLORS = { Hard: '#3b82f6', Clay: '#f97316', Grass: '#22c55e' };
const LEVEL_COLORS   = { G: '#7c3aed', M: '#1d4ed8', F: '#0891b2', A: '#374151' };

const API_URL = process.env.REACT_APP_API_URL || '/api';

const LEAGUE_FLAG_CODE = {
    EPL:        'gb-eng',
    La_liga:    'es',
    Serie_A:    'it',
    Bundesliga: 'de',
    Ligue_1:    'fr',
};

function StatsSection({ accuracy }) {
    const { t } = useTranslation();

    if (!accuracy || !accuracy.total_predictions) return null;

    const pct = accuracy.overall_accuracy * 100;
    const accColor = pct >= 60 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';

    const BREAKDOWN_CONFIG = [
        { key: 'home', label: t('historial.breakdown_home'), color: '#1d4ed8' },
        { key: 'draw', label: t('historial.breakdown_draw'), color: '#d97706' },
        { key: 'away', label: t('historial.breakdown_away'), color: '#dc2626' },
    ];

    return (
        <div className="historial-stats">
            <div className="accuracy-card-main">
                <div className="accuracy-main-left">
                    <div className="accuracy-main-label">{t('historial.accuracy_label')}</div>
                    <div className="accuracy-main-sub">
                        {t('common.correct_of', {
                            correct: accuracy.correct_predictions,
                            total: accuracy.total_predictions,
                        })}
                    </div>
                </div>
                <div className="accuracy-main-number" style={{ color: accColor }}>
                    {pct.toFixed(2)}%
                </div>
            </div>

            <div className="breakdown-grid">
                {BREAKDOWN_CONFIG.map(({ key, label, color }) => {
                    const data = accuracy.by_result[key];
                    return (
                        <div key={key} className="breakdown-card" style={{ borderLeftColor: color }}>
                            <div className="breakdown-header">
                                <span className="breakdown-dot" style={{ background: color }} />
                                <span className="breakdown-label">{label}</span>
                            </div>
                            <div className="breakdown-number">{(data.accuracy * 100).toFixed(1)}%</div>
                            <div className="stat-row">
                                <span>{t('historial.correct_label')}</span>
                                <span className="stat-value">{data.correct}/{data.total}</span>
                            </div>
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${data.accuracy * 100}%`, background: color }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function HistoryRow({ match, onSelect }) {
    const { t } = useTranslation();
    const isCorrect = match.is_correct;
    const flagCode = LEAGUE_FLAG_CODE[match.league];

    return (
        <div
            className={`history-row ${isCorrect ? 'correct' : 'incorrect'}`}
            onClick={() => onSelect(match.match_id)}
        >
            <div className="history-date">
                <div>{formatDate(match.date)}</div>
                <div className="history-league">
                    {flagCode && (
                        <span className={`fi fi-${flagCode}`} style={{ marginRight: '5px', fontSize: '0.75rem' }} />
                    )}
                    {t(`leagues.${match.league}`, { defaultValue: match.league })}
                </div>
            </div>
            <div className="history-match">
                <span className="team">{match.home_team}</span>
                <span className="score">{match.home_score ?? '?'} - {match.away_score ?? '?'}</span>
                <span className="team">{match.away_team}</span>
            </div>
            <div className="history-prediction">
                <span className="label">{t('historial.pred_label')}</span>
                <span className={`result-badge ${match.predicted_result}`}>
                    {getResultLabel(match.predicted_result)}
                </span>
                {match.confidence && (
                    <span
                        className="confidence-badge"
                        style={{
                            backgroundColor: getConfidenceColor(match.confidence),
                            fontSize: '0.72rem',
                            padding: '2px 8px',
                        }}
                    >
                        {(match.confidence * 100).toFixed(0)}%
                    </span>
                )}
            </div>
            <div className="history-actual">
                <span className="label">{t('historial.actual_label')}</span>
                <span className={`result-badge ${match.actual_result}`}>
                    {getResultLabel(match.actual_result)}
                </span>
            </div>
            <div className="history-verdict">
                <span className={`verdict-icon ${isCorrect ? 'ok' : 'fail'}`}>
                    {isCorrect ? '✓' : '✗'}
                </span>
            </div>
        </div>
    );
}

// ── Baseball Historial ────────────────────────────────────────────────────

function BaseballStatsSection({ stats }) {
    const { t } = useTranslation();
    if (!stats || stats.total === 0) return null;
    const pct = stats.accuracy * 100;
    const accColor = pct >= 60 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';

    return (
        <div className="historial-stats">
            <div className="accuracy-card-main">
                <div className="accuracy-main-left">
                    <div className="accuracy-main-label">{t('historial.accuracy_label')}</div>
                    <div className="accuracy-main-sub">
                        {t('common.correct_of', { correct: stats.correct, total: stats.total })}
                    </div>
                </div>
                <div className="accuracy-main-number" style={{ color: accColor }}>
                    {pct.toFixed(2)}%
                </div>
            </div>
            <div className="breakdown-grid">
                <div className="breakdown-card" style={{ borderLeftColor: '#22c55e' }}>
                    <div className="breakdown-header">
                        <span className="breakdown-dot" style={{ background: '#22c55e' }} />
                        <span className="breakdown-label">Home wins</span>
                    </div>
                    <div className="breakdown-number" style={{ color: accColor }}>{pct.toFixed(1)}%</div>
                    <div className="stat-row">
                        <span>{t('historial.correct_label')}</span>
                        <span className="stat-value">{stats.correct}/{stats.total}</span>
                    </div>
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${pct}%`, background: '#22c55e' }} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function BaseballHistoryRow({ match }) {
    const { t } = useTranslation();
    const isCorrect = match.is_correct;
    const conf = match.confidence != null
        ? Math.round(match.confidence * 100)
        : Math.round((match.prob_home > match.prob_away ? match.prob_home : match.prob_away) * 100);
    const predictedTeam = match.predicted_winner === 'home' ? match.home_team : match.away_team;
    const actualTeam   = match.result === 'home' ? match.home_team : match.away_team;

    return (
        <div className={`history-row ${isCorrect === true ? 'correct' : isCorrect === false ? 'incorrect' : ''}`}>
            <div className="history-date">
                <div>{formatDate(match.game_date)}</div>
                <div className="history-league" style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)' }}>
                    MLB
                </div>
            </div>
            <div className="history-match">
                <span className="team">{match.home_team}</span>
                <span className="score">{match.home_score ?? '?'} - {match.away_score ?? '?'}</span>
                <span className="team">{match.away_team}</span>
            </div>
            <div className="history-prediction">
                <span className="label">{t('historial.pred_label')}</span>
                <span className="result-badge H" style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {predictedTeam}
                </span>
                <span className="confidence-badge"
                    style={{ backgroundColor: getConfidenceColor(conf / 100), fontSize: '0.72rem', padding: '2px 8px' }}>
                    {conf}%
                </span>
            </div>
            <div className="history-actual">
                <span className="label">{t('historial.actual_label')}</span>
                <span className="result-badge A" style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {actualTeam}
                </span>
            </div>
            <div className="history-verdict">
                <span className={`verdict-icon ${isCorrect ? 'ok' : 'fail'}`}>
                    {isCorrect ? '✓' : '✗'}
                </span>
            </div>
        </div>
    );
}

function BaseballHistorialContent() {
    const { t } = useTranslation();
    const [activeView, setActiveView] = useState('stats');
    const [filter, setFilter]         = useState('all');
    const [history, setHistory]       = useState([]);
    const [stats, setStats]           = useState(null);
    const [loading, setLoading]       = useState(false);
    const [page, setPage]             = useState(0);
    const [total, setTotal]           = useState(0);
    const PAGE_SIZE = 50;

    const fetchStats = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/baseball/stats`);
            const d = res.data;
            const acc = d.accuracy || {};
            setStats({
                total:    acc.total || 0,
                correct:  acc.correct || 0,
                accuracy: acc.rate || 0,
            });
        } catch (e) { console.error(e); }
    }, []);

    const fetchHistory = useCallback(async (p = 0) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/baseball/results?limit=${PAGE_SIZE}&offset=${p * PAGE_SIZE}`);
            const all = res.data || [];
            const filtered = filter === 'all' ? all
                : filter === 'correct'   ? all.filter(m => m.is_correct === true)
                : all.filter(m => m.is_correct === false);
            setHistory(filtered);
            setTotal(filtered.length);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => {
        if (activeView === 'history') { setPage(0); fetchHistory(0); }
    }, [filter, activeView, fetchHistory]);

    const FILTERS = [
        { id: 'all',       label: t('historial.filter_all') },
        { id: 'correct',   label: t('historial.filter_correct') },
        { id: 'incorrect', label: t('historial.filter_incorrect') },
    ];

    return (
        <>
            <div className="accuracy-subtabs">
                <button className={`subtab ${activeView === 'stats' ? 'active' : ''}`}
                    onClick={() => setActiveView('stats')}>
                    {t('historial.tab_stats')}
                </button>
                <button className={`subtab ${activeView === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveView('history')}>
                    {t('historial.tab_predictions')}
                </button>
            </div>

            {activeView === 'stats' && (
                stats?.total > 0
                    ? <BaseballStatsSection stats={stats} />
                    : <div className="empty-state">
                        <p>{t('historial.empty_stats')}</p>
                        <p className="subtitle">{t('historial.empty_stats_sub')}</p>
                      </div>
            )}

            {activeView === 'history' && (
                <>
                    <div className="history-filters">
                        {FILTERS.map(f => (
                            <button key={f.id}
                                className={`filter-btn ${filter === f.id ? 'active' : ''}`}
                                onClick={() => setFilter(f.id)}>
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="empty-state"><p>{t('common.loading')}</p></div>
                    ) : history.length === 0 ? (
                        <div className="empty-state">
                            <p>{t('historial.empty_history')}</p>
                            <p className="subtitle">{t('historial.empty_history_sub')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="history-summary">
                                <span>{t('common.predictions_count', { count: total })}</span>
                            </div>
                            <div className="history-header">
                                <span>{t('historial.col_date')}</span>
                                <span>{t('historial.col_match')}</span>
                                <span>{t('historial.col_prediction')}</span>
                                <span>{t('historial.col_actual')}</span>
                                <span></span>
                            </div>
                            <div className="history-list">
                                {history.map((m, i) => <BaseballHistoryRow key={i} match={m} />)}
                            </div>
                            <div className="pagination">
                                <button className="page-btn" disabled={page === 0}
                                    onClick={() => { const p = page - 1; setPage(p); fetchHistory(p); }}>
                                    {t('historial.prev')}
                                </button>
                                <span className="page-info">
                                    {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} / {total}
                                </span>
                                <button className="page-btn" disabled={(page + 1) * PAGE_SIZE >= total}
                                    onClick={() => { const p = page + 1; setPage(p); fetchHistory(p); }}>
                                    {t('historial.next')}
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
}

// ── Tennis Historial ──────────────────────────────────────────────────────

function TennisStatsSection({ stats }) {
    const { t } = useTranslation();
    if (!stats || stats.total === 0) return null;
    const pct = stats.accuracy * 100;
    const accColor = pct >= 65 ? '#10b981' : pct >= 55 ? '#f59e0b' : '#ef4444';

    return (
        <div className="historial-stats">
            <div className="accuracy-card-main">
                <div className="accuracy-main-left">
                    <div className="accuracy-main-label">{t('historial.accuracy_label')}</div>
                    <div className="accuracy-main-sub">
                        {t('common.correct_of', { correct: stats.correct, total: stats.total })}
                    </div>
                </div>
                <div className="accuracy-main-number" style={{ color: accColor }}>
                    {pct.toFixed(2)}%
                </div>
            </div>
            <div className="breakdown-grid">
                {(stats.by_surface || []).map(s => (
                    <div key={s.surface} className="breakdown-card"
                        style={{ borderLeftColor: SURFACE_COLORS[s.surface] || '#6b7280' }}>
                        <div className="breakdown-header">
                            <span className="breakdown-dot"
                                style={{ background: SURFACE_COLORS[s.surface] || '#6b7280' }} />
                            <span className="breakdown-label">{t(`tennis.surface_${s.surface.toLowerCase()}`, { defaultValue: s.surface })}</span>
                        </div>
                        <div className="breakdown-number">{(s.accuracy * 100).toFixed(1)}%</div>
                        <div className="stat-row">
                            <span>{t('historial.correct_label')}</span>
                            <span className="stat-value">{s.correct}/{s.total}</span>
                        </div>
                        <div className="progress-bar">
                            <div className="progress-fill"
                                style={{ width: `${s.accuracy * 100}%`,
                                         background: SURFACE_COLORS[s.surface] || '#6b7280' }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TennisPredictionModal({ match: m, onClose }) {
    const { t } = useTranslation();

    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [onClose]);

    if (!m) return null;

    const probP1   = m.prob_winner;
    const probP2   = m.prob_loser;
    const margin   = Math.abs(probP1 - probP2);
    const surfColor = SURFACE_COLORS[m.surface] || '#6b7280';

    const getMarginLabel = (mg) => {
        if (mg < 0.10) return t('modal.margin_tight');
        if (mg < 0.20) return t('modal.margin_moderate');
        return t('modal.margin_clear');
    };

    const players = [
        { name: m.p1_name, prob: probP1 },
        { name: m.p2_name, prob: probP2 },
    ];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{t('modal.title')}</h3>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    {/* Jugadores + resultado */}
                    <div className="modal-match">
                        <div className="modal-team">{m.p1_name}</div>
                        <div className="modal-score">
                            {m.score && m.score.trim() ? m.score : t('common.vs')}
                        </div>
                        <div className="modal-team">{m.p2_name}</div>
                    </div>

                    {/* Torneo + fecha */}
                    <div className="modal-date">
                        {m.tournament}{m.round ? ` · ${m.round}` : ''} ·{' '}
                        <span style={{ background: surfColor, color: 'white',
                                       fontSize: '0.65rem', fontWeight: 700,
                                       padding: '1px 6px', borderRadius: '8px', marginLeft: '4px' }}>
                            {t(`tennis.surface_${(m.surface||'').toLowerCase()}`, { defaultValue: m.surface })}
                        </span>
                        {' · '}{m.date}
                    </div>

                    {/* Veredicto */}
                    <div className={`modal-verdict ${m.is_correct ? 'correct' : 'incorrect'}`}>
                        {m.is_correct ? t('modal.correct') : t('modal.incorrect')}
                    </div>

                    {/* Predicción → Real */}
                    <div className="modal-results-row">
                        <div className="modal-result-box">
                            <span className="modal-result-label">{t('modal.prediction_label')}</span>
                            <span className="modal-result-badge" style={{ background: '#1d4ed8', fontSize: '0.8rem', padding: '6px 12px' }}>
                                {m.predicted_winner}
                            </span>
                        </div>
                        <div className="modal-result-arrow">→</div>
                        <div className="modal-result-box">
                            <span className="modal-result-label">{t('modal.actual_label')}</span>
                            <span className="modal-result-badge"
                                  style={{ background: m.is_correct ? '#059669' : '#dc2626',
                                           fontSize: '0.8rem', padding: '6px 12px' }}>
                                {m.actual_winner || '—'}
                            </span>
                        </div>
                    </div>

                    {/* Probabilidades */}
                    <div className="modal-probs-section">
                        <h4>{t('modal.probabilities')}</h4>
                        {players.map(({ name, prob }) => {
                            const isPredicted = name === m.predicted_winner;
                            const isActual    = name === m.actual_winner;
                            return (
                                <div key={name} className={`modal-prob-row ${isPredicted ? 'predicted' : ''}`}>
                                    <div className="modal-prob-label" style={{ fontSize: '0.78rem' }}>
                                        {name}
                                        {isPredicted && <span className="tag predicted-tag">{t('modal.chosen')}</span>}
                                        {isActual    && <span className="tag actual-tag">{t('modal.real')}</span>}
                                    </div>
                                    <div className="modal-prob-bar-wrap">
                                        <div className="modal-prob-bar-fill" style={{
                                            width: `${(prob * 100).toFixed(1)}%`,
                                            background: isPredicted ? surfColor : '#9ca3af',
                                            opacity: isPredicted ? 1 : 0.55,
                                        }} />
                                    </div>
                                    <div className="modal-prob-value">{(prob * 100).toFixed(1)}%</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Margen */}
                    <div className="modal-margin">
                        <span className="margin-label">{t('modal.margin_label')}</span>
                        <span className={`margin-value ${margin < 0.10 ? 'tight' : 'clear'}`}>
                            {(margin * 100).toFixed(1)}% {getMarginLabel(margin)}
                        </span>
                    </div>

                    <div className="modal-footer-info">{t('modal.model_label')} xgb_atp_v1</div>
                </div>
            </div>
        </div>
    );
}

function TennisHistoryRow({ match, onSelect }) {
    const { t } = useTranslation();
    const isCorrect = match.is_correct;
    const conf = Math.round(match.confidence * 100);

    return (
        <div className={`history-row ${isCorrect ? 'correct' : 'incorrect'}`}
             onClick={() => onSelect(match)}
             style={{ cursor: 'pointer' }}>
            <div className="history-date">
                <div>{formatDate(match.date)}</div>
                <div className="history-league" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span className="pick-surface-badge"
                        style={{ background: SURFACE_COLORS[match.surface] || '#6b7280',
                                 fontSize: '0.6rem', padding: '1px 5px', borderRadius: '8px',
                                 color: 'white', fontWeight: 600 }}>
                        {match.surface}
                    </span>
                    <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)' }}>
                        {match.level_name}
                    </span>
                </div>
            </div>
            <div className="history-match" style={{ flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
                <span className="team" style={{ fontWeight: 500 }}>{match.p1_name}</span>
                <span className="score" style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)' }}>
                    {match.tournament}{match.round ? ` · ${match.round}` : ''}
                </span>
                <span className="team" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>{match.p2_name}</span>
            </div>
            <div className="history-prediction">
                <span className="label">{t('historial.pred_label')}</span>
                <span className="result-badge H" style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {match.predicted_winner}
                </span>
                <span className="confidence-badge"
                    style={{ backgroundColor: getConfidenceColor(match.confidence),
                             fontSize: '0.72rem', padding: '2px 8px' }}>
                    {conf}%
                </span>
            </div>
            <div className="history-actual">
                <span className="label">{t('historial.actual_label')}</span>
                <span className="result-badge A" style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {match.actual_winner || '—'}
                </span>
            </div>
            <div className="history-verdict">
                <span className={`verdict-icon ${isCorrect ? 'ok' : 'fail'}`}>
                    {isCorrect ? '✓' : '✗'}
                </span>
            </div>
        </div>
    );
}

function TennisHistorialContent({ season, onSeasonChange }) {
    const { t } = useTranslation();
    const [activeView,     setActiveView]     = useState('stats');
    const [filter,         setFilter]         = useState('all');
    const [history,        setHistory]        = useState([]);
    const [stats,          setStats]          = useState(null);
    const [loading,        setLoading]        = useState(false);
    const [page,           setPage]           = useState(0);
    const [total,          setTotal]          = useState(0);
    const [selectedMatch,  setSelectedMatch]  = useState(null);
    const PAGE_SIZE = 50;

    // Tennis years available
    const years = ['2024', '2025', '2026'];

    const fetchStats = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (season && season !== 'all') params.set('year', season);
            const res = await axios.get(`${API_URL}/tennis/analysis?${params}`);
            const d = res.data;
            setStats({
                total:      d.total,
                correct:    Math.round((d.accuracy || 0) * d.total),
                accuracy:   d.accuracy || 0,
                by_surface: d.by_surface || [],
            });
        } catch (e) { console.error(e); }
    }, [season]);

    const fetchHistory = useCallback(async (p = 0) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: PAGE_SIZE, offset: p * PAGE_SIZE, filter });
            if (season && season !== 'all') params.set('year', season);
            const res = await axios.get(`${API_URL}/tennis/history?${params}`);
            setHistory(res.data.history || []);
            setTotal(res.data.total || 0);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [filter, season]);

    const refreshAll = useCallback(() => {
        fetchStats();
        if (activeView === 'history') fetchHistory(0);
    }, [fetchStats, fetchHistory, activeView]);

    useEffect(() => { fetchStats(); }, [season, fetchStats]);
    useEffect(() => {
        setPage(0); fetchHistory(0);
    }, [filter, season, activeView, fetchHistory]);

    const FILTERS = [
        { id: 'all',       label: t('historial.filter_all') },
        { id: 'correct',   label: t('historial.filter_correct') },
        { id: 'incorrect', label: t('historial.filter_incorrect') },
    ];

    return (
        <>
            {/* Season selector por año */}
            <div className="season-selector" style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button className={`season-pill${season === 'all' ? ' active' : ''}`}
                    onClick={() => onSeasonChange('all')}>
                    {t('common.all_seasons')}
                </button>
                {years.map(y => (
                    <button key={y}
                        className={`season-pill${season === y ? ' active' : ''}`}
                        onClick={() => onSeasonChange(y)}>
                        {y}
                    </button>
                ))}
                <button className="refresh-button" onClick={refreshAll} style={{ marginLeft: 'auto' }}>
                    {t('common.refresh')}
                </button>
            </div>

            <div className="accuracy-subtabs">
                <button className={`subtab ${activeView === 'stats' ? 'active' : ''}`}
                    onClick={() => setActiveView('stats')}>
                    {t('historial.tab_stats')}
                </button>
                <button className={`subtab ${activeView === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveView('history')}>
                    {t('historial.tab_predictions')}
                </button>
            </div>

            {activeView === 'stats' && (
                stats?.total > 0
                    ? <TennisStatsSection stats={stats} />
                    : <div className="empty-state">
                        <p>{t('historial.empty_stats')}</p>
                        <p className="subtitle">{t('historial.empty_stats_sub')}</p>
                      </div>
            )}

            {activeView === 'history' && (
                <>
                    <div className="history-filters">
                        {FILTERS.map(f => (
                            <button key={f.id}
                                className={`filter-btn ${filter === f.id ? 'active' : ''}`}
                                onClick={() => setFilter(f.id)}>
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="empty-state"><p>{t('common.loading')}</p></div>
                    ) : history.length === 0 ? (
                        <div className="empty-state">
                            <p>{t('historial.empty_history')}</p>
                            <p className="subtitle">{t('historial.empty_history_sub')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="history-summary">
                                <span>{t('common.predictions_count', { count: total })}</span>
                            </div>
                            <div className="history-header">
                                <span>{t('historial.col_date')}</span>
                                <span>{t('historial.col_match')}</span>
                                <span>{t('historial.col_prediction')}</span>
                                <span>{t('historial.col_actual')}</span>
                                <span></span>
                            </div>
                            <div className="history-list">
                                {history.map(m => (
                                    <TennisHistoryRow key={m.match_id} match={m} onSelect={setSelectedMatch} />
                                ))}
                            </div>
                            <div className="pagination">
                                <button className="page-btn" disabled={page === 0}
                                    onClick={() => { const p = page-1; setPage(p); fetchHistory(p); }}>
                                    {t('historial.prev')}
                                </button>
                                <span className="page-info">
                                    {page * PAGE_SIZE + 1}–{Math.min((page+1)*PAGE_SIZE, total)} / {total}
                                </span>
                                <button className="page-btn" disabled={(page+1)*PAGE_SIZE >= total}
                                    onClick={() => { const p = page+1; setPage(p); fetchHistory(p); }}>
                                    {t('historial.next')}
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        {selectedMatch && (
            <TennisPredictionModal match={selectedMatch} onClose={() => setSelectedMatch(null)} />
        )}
        </>
    );
}

// ── Football + Tennis router ──────────────────────────────────────────────

export function HistorialPage({
    fetchPredictionDetail,
    league,
    accuracy,
    onRefreshAccuracy,
    availableSeasons = [],
    season = 'all',
    onSeasonChange,
    isTennis = false,
    isBaseball = false,
}) {
    const { t } = useTranslation();
    const [history, setHistory]             = useState([]);
    const [filter, setFilter]               = useState('all');
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [loading, setLoading]             = useState(false);
    const [activeView, setActiveView]       = useState('stats');
    const [page, setPage]                   = useState(0);
    const [totalCount, setTotalCount]       = useState(0);
    const PAGE_SIZE = 50;

    const fetchHistory = useCallback(async (p = 0) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: PAGE_SIZE, offset: p * PAGE_SIZE, filter });
            if (league) params.append('league', league);
            if (season && season !== 'all') {
                const startYear = parseInt(season.split('-')[0]);
                params.append('date_from', `${startYear}-08-01`);
                params.append('date_to',   `${startYear + 1}-07-31`);
            }
            const res = await axios.get(`${API_URL}/accuracy/history?${params}`);
            setHistory(res.data.history || []);
            setTotalCount(res.data.total_count || 0);
        } catch (err) {
            console.error('Error fetching history:', err);
        } finally {
            setLoading(false);
        }
    }, [filter, league, season]);

    // FIX: fetchHistory agregado como dependencia para que React siempre use
    // la versión actualizada del closure (con el season/league/filter correcto).
    // Antes, al cambiar season, fetchHistory se recreaba pero el useEffect
    // usaba la versión stale porque no la tenía en sus deps.
    useEffect(() => {
        if (activeView === 'history') {
            setPage(0);
            fetchHistory(0);
        }
    }, [filter, league, season, activeView, fetchHistory]);

    // Cuando cambian liga o temporada, refrescar también las stats
    useEffect(() => {
        if (isBaseball || isTennis) return;
        onRefreshAccuracy(season);
    }, [league, season, isBaseball, isTennis]);

    const handlePrev = () => {
        const newPage = page - 1;
        setPage(newPage);
        fetchHistory(newPage);
    };

    const handleNext = () => {
        const newPage = page + 1;
        setPage(newPage);
        fetchHistory(newPage);
    };

    const handleRowClick = async (matchId) => {
        try {
            const detail = await fetchPredictionDetail(matchId);
            setSelectedMatch(detail);
        } catch (err) {
            console.error('Error fetching detail:', err);
        }
    };

    const flagCode = league ? LEAGUE_FLAG_CODE[league] : null;

    const FILTERS = [
        { id: 'all',       label: t('historial.filter_all') },
        { id: 'correct',   label: t('historial.filter_correct') },
        { id: 'incorrect', label: t('historial.filter_incorrect') },
    ];

    if (isBaseball) {
        return (
            <div className="historial-section">
                <div className="section-header">
                    <h2>{t('historial.title')} — Baseball / MLB</h2>
                </div>
                <BaseballHistorialContent />
            </div>
        );
    }

    if (isTennis) {
        return (
            <div className="historial-section">
                <div className="section-header">
                    <h2>{t('historial.title')} — {t('leagues.Tennis')}</h2>
                </div>
                <TennisHistorialContent season={season} onSeasonChange={onSeasonChange} />
            </div>
        );
    }

    return (
        <div className="historial-section">
            <div className="section-header">
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {t('historial.title')}
                    {league && (
                        <span className="league-tag-header" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {flagCode && <span className={`fi fi-${flagCode}`} />}
                            {t(`leagues.${league}`, { defaultValue: league })}
                        </span>
                    )}
                </h2>
                <button
                    onClick={() => { onRefreshAccuracy(); fetchHistory(page); }}
                    className="refresh-button"
                >
                    {t('common.refresh')}
                </button>
            </div>

            <div className="season-selector" style={{ marginBottom: '12px' }}>
                <button
                    className={`season-pill${season === 'all' ? ' active' : ''}`}
                    onClick={() => onSeasonChange('all')}
                >
                    {t('common.all_seasons')}
                </button>
                {availableSeasons.map(s => (
                    <button
                        key={s}
                        className={`season-pill${season === s ? ' active' : ''}`}
                        onClick={() => onSeasonChange(s)}
                    >
                        {s}
                    </button>
                ))}
            </div>

            <div className="accuracy-subtabs">
                <button
                    className={`subtab ${activeView === 'stats' ? 'active' : ''}`}
                    onClick={() => setActiveView('stats')}
                >
                    {t('historial.tab_stats')}
                </button>
                <button
                    className={`subtab ${activeView === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveView('history')}
                >
                    {t('historial.tab_predictions')}
                </button>
            </div>

            {activeView === 'stats' && (
                accuracy?.total_predictions > 0 ? (
                    <StatsSection accuracy={accuracy} />
                ) : (
                    <div className="empty-state">
                        <p>{t('historial.empty_stats')}</p>
                        <p className="subtitle">{t('historial.empty_stats_sub')}</p>
                    </div>
                )
            )}

            {activeView === 'history' && (
                <>
                    <div className="history-filters">
                        {FILTERS.map(f => (
                            <button
                                key={f.id}
                                className={`filter-btn ${filter === f.id ? 'active' : ''}`}
                                onClick={() => setFilter(f.id)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="empty-state"><p>{t('common.loading')}</p></div>
                    ) : history.length === 0 ? (
                        <div className="empty-state">
                            <p>{t('historial.empty_history')}</p>
                            <p className="subtitle">{t('historial.empty_history_sub')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="history-summary">
                                <span>{t('common.predictions_count', { count: totalCount })}</span>
                                <span className="separator">·</span>
                                <span className="hint-text">{t('common.click_detail')}</span>
                            </div>

                            <div className="history-header">
                                <span>{t('historial.col_date')}</span>
                                <span>{t('historial.col_match')}</span>
                                <span>{t('historial.col_prediction')}</span>
                                <span>{t('historial.col_actual')}</span>
                                <span></span>
                            </div>

                            <div className="history-list">
                                {history.map(match => (
                                    <HistoryRow key={match.match_id} match={match} onSelect={handleRowClick} />
                                ))}
                            </div>

                            <div className="pagination">
                                <button
                                    className="page-btn"
                                    onClick={handlePrev}
                                    disabled={page === 0}
                                >
                                    {t('historial.prev')}
                                </button>
                                <span className="page-info">
                                    {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} de {totalCount}
                                </span>
                                <button
                                    className="page-btn"
                                    onClick={handleNext}
                                    disabled={(page + 1) * PAGE_SIZE >= totalCount}
                                >
                                    {t('historial.next')}
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}

            {selectedMatch && (
                <PredictionDetailModal match={selectedMatch} onClose={() => setSelectedMatch(null)} />
            )}
        </div>
    );
}